import React, { useEffect, useRef, useState } from 'react'
import { LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined, UserOutlined } from '@ant-design/icons'
import { Button, Dropdown, Flex, Layout, theme, Typography } from 'antd'
import { Link, useLocation, useNavigate } from 'react-router'
import { ImpersonateUserModal } from '@/common/layouts/components/impersonate-user-modal.tsx'
import { NavMenu } from '@/common/layouts/NavMenu.tsx'
import { useAuth } from '@/common/hooks/useAuth.ts'
import { HcRole } from '@/common/types/auth.def.ts'
import { routes } from '@/utils/routes.ts'
import { EmployeeSearch } from '@/common/components/AntdForm/AntdFormEmployeeSearch.tsx'
import { EmployeePublicInfoProvider } from '@/common/hooks/useEmployeePublicInfo.tsx'
import { cn } from '@/utils/style.ts'
import { AppEnv } from '@/utils/env.ts'
import { hasOriginalAuth, stopImpersonation } from '@/features/auth/auth.middleware.ts'

const { Header, Sider, Content } = Layout

export const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isHc, isImpersonated, impersonatedUsername, checkHasAllPermissions } = useAuth()
  const [collapsed, setCollapsed] = useState(false)
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isDevEnv = AppEnv.isDevelopment
  const canImpersonate = checkHasAllPermissions([{ moduleName: 'assume-identity', functionName: 'assume-user' }])

  // `Content` is the app's scroll container rather than the window, so React
  // Router's scroll handling does not reach it — without this, a new page
  // inherits the previous page's scroll offset and opens part-way down.
  const contentRef = useRef<HTMLElement>(null)
  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, left: 0 })
  }, [pathname])

  const { token: { colorBgContainer } } = theme.useToken()

  return (
    <EmployeePublicInfoProvider>
      <Flex
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'row',
          position: 'relative',
          flexWrap: 'nowrap',
        }}
      >
        <Layout>
          <Header className="relative flex items-center justify-between w-full h-[48px] px-4 bg-stone-100">
            <Flex gap="small" align="center">
              <Button
                type="text"
                htmlType="button"
                className="bg-gray-200"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(prev => !prev)}
              />
              <Link to="/" className="absolute left-1/2 -translate-x-1/2">
                <Flex
                  gap="small"
                  align="center"
                >
                  <Typography.Title
                    level={4}
                    className={cn('!mb-0 !text-white font-bold px-3 rounded-md', {
                      'bg-cyan-500': isDevEnv,
                      'bg-red-500': !isDevEnv,
                    })}
                  >HC Portal{isDevEnv ? ' - DEV' : ''}</Typography.Title>
                </Flex>
              </Link>
            </Flex>
            <Flex gap="small">
              {isHc ? <EmployeeSearch
                className="w-80"
                onChange={eid => {
                  if (eid) {
                    navigate(routes.employeeData(eid as string))
                  }
                }}
              /> : null}
              {canImpersonate ? <ImpersonateUserModal /> : null}
              <Dropdown
                menu={{
                  rootClassName: 'min-w-32',
                  items: [
                    {
                      label: 'Logout',
                      key: 'logout',
                      icon: <LogoutOutlined />,
                      onClick: () => navigate(routes.logout),
                    },
                    ...(user?.roles?.includes(HcRole.Developer) ? [
                      {
                        label: 'Copy Token',
                        key: 'copyToken',
                        onClick: () => navigator.clipboard.writeText(user.token),
                      },
                      {
                        label: 'Okta Logout',
                        key: 'oktaLogout',
                        danger: true,
                        onClick: () => window.open(import.meta.env.VITE_OKTA_LOGOUT_URL, '_blank'),
                      },
                    ] : []),
                  ],
                }}
              >
                <Button
                  type="text"
                  htmlType="button"
                  className="bg-gray-100"
                  icon={<UserOutlined />}
                />
              </Dropdown>
            </Flex>
          </Header>
          <Layout className="relative">
            <RootLayoutSider
              collapsed={collapsed}
              onNavItemClick={(key) => {
                // the reward exercise pages are wide, so the menu steps aside once one is opened
                if (key === 'rewardExercise') setCollapsed(true)
              }}
            />
            {isImpersonated ? (
              <Flex
                align="center"
                gap="small"
                className="bg-red-500 absolute top-0 z-50 p-1 px-2 w-fit left-1/2 -translate-x-1/2 rounded-md"
              >
                <Typography.Text className="text-md text-white font-bold">
                  You are now impersonated as:
                </Typography.Text>
                <Typography.Text className="text-md text-red-500 font-bold bg-white px-1 border rounded-md">
                  {impersonatedUsername}
                </Typography.Text>
                {hasOriginalAuth() ? (
                  <Button size="small" type="dashed" danger onClick={stopImpersonation}>Stop Impersonation</Button>
                ) : null}
              </Flex>
            ) : null}
            <Content
              ref={contentRef}
              style={{
                overflow: 'auto',
                height: '100%',
                background: colorBgContainer,
              }}
            >
              {children}
            </Content>
          </Layout>
        </Layout>
      </Flex>
    </EmployeePublicInfoProvider>
  )
}

const RootLayoutSider: React.FC<{ collapsed: boolean, onNavItemClick: (key: string) => void }> = ({
  collapsed,
  onNavItemClick,
}) => {
  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      theme="light"
    >
      <NavMenu onItemClick={onNavItemClick} />
    </Sider>
  )
}